import React, { useCallback, useEffect, useState } from 'react';
import Avatar from '@material-ui/core/Avatar';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ImageIcon from '@material-ui/icons/Image';
import Paper from '@material-ui/core/Paper';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import { makeStyles } from '@material-ui/core/styles';
import { deepOrange } from '@material-ui/core/colors';
import Badge from '@material-ui/core/Badge';
import IconButton from '@material-ui/core/IconButton';
import NotificationIcon from '@material-ui/icons/Notifications';
import { useAuth, useRoutes, useApi } from '../../hooks';
import { Notification, Emargement } from '../../types';
import Button from '@material-ui/core/Button';

const useStyles = makeStyles((theme) => ({
  avatar: {
    color: theme.palette.getContrastText(deepOrange[500]),
    backgroundColor: deepOrange[500],
    width: theme.spacing(4),
    height: theme.spacing(4),

    cursor: 'pointer',
  },
  bigAvatar: {
    color: theme.palette.getContrastText(deepOrange[500]),
    backgroundColor: deepOrange[500],
    width: theme.spacing(6),
    height: theme.spacing(6),
  },
  container: {
    width: 400,
  },
  infos: {
    display: 'flex',
    padding: theme.spacing(2),
  },
  names: {
    flex: 1,
    marginLeft: theme.spacing(1),
  },
  action: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: theme.spacing(2),
  },
  empty: {
    padding: theme.spacing(2),
  },
}));

type NoficationDisplay = {
  reportId?: number | string;
  title: string;
  subtitle: string;
  type: string;
  date: string;
  path: string;
};

export const Notifications: React.FC = () => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { getNotifications, updateNotifications, deleteNotifications, getEmargement } = useApi();
  const { auth, isAgent } = useAuth();
  const { paths, getPathByTheme, navigate, currentPathname } = useRoutes();
  const [notifications, setNotifications] = useState<NoficationDisplay[]>([]);
  const [isUpdating, setUpdating] = useState(false);
  const enabled = !!notifications.length;

  const handleClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const handleOnDelete = useCallback(async () => {
    try {
      await deleteNotifications();
      setNotifications((notifications) =>
        (notifications || []).filter((notification) => notification.type === 'Emargement')
      );
    } catch (error) { }
  }, [deleteNotifications]);

  const getNotificationLabel = useCallback((type) => {
    if (type === 'Création') {
      return 'Un nouveau rapport est disponible';
    }
    if (type === 'Réponse') {
      return 'Une réponse est disponible';
    }

    if (type === 'Lecture') {
      return 'Un rapport a été lu';
    }
  }, []);

  const handleOnNotificationClick = useCallback(
    async ({ reportId, type: notificationType, path, authorCPCode }) => {
      handleClose();
      navigate(path)();

      try {
        setUpdating(true);
        if (reportId) {
          await updateNotifications({
            reportId,
            notificationType,
            userName: auth.userName,
            isRead: true,
            authorCPCode,
          });
        }
        setUpdating(false);
        setNotifications((notifications) =>
          notifications.filter(
            (notif) => !(notif.type === notificationType && notif.reportId === reportId)
          )
        );
      } catch (error) {
        setUpdating(false);
      }
    },
    [navigate, updateNotifications, auth, handleClose]
  );

  const getNoficationList = useCallback(async () => {
    try {
      let requests: any[] = [getNotifications()];
      if (isAgent) {
        requests.push(getEmargement({ isValidated: false }));
      }
      const [simpleNotifs, emargements] = await Promise.all(requests);
      const simpleNotifications = (simpleNotifs || []).map((notification: Notification) => ({
        ...notification,
        reportId: notification.reportId,
        title: getNotificationLabel(notification.notificationType) || '',
        subtitle: getPathByTheme(notification.themeShortCode)?.label || '',
        type: notification.notificationType,
        date: notification.notificationDate || '',
        path: `${getPathByTheme(notification.themeShortCode)?.path}/${notification.reportId}` || '',
      }));

      const emargementNotifications = ((emargements && emargements.emargements) || []).map(
        (emargement: Emargement) => ({
          title: 'Un nouvel émargement est disponible',
          subtitle: 'Emargement',
          type: 'Emargement',
          date: emargement.creationDate,
          path: `${paths.emargement.path}/${emargement.id}`,
        })
      );
      const notifications = [...emargementNotifications, ...simpleNotifications];
      setNotifications(notifications);
    } catch (error) {
      console.log('error', error);
    }
  }, [
    getEmargement,
    getNotifications,
    getNotificationLabel,
    getPathByTheme,
    isAgent,
    paths.emargement,
  ]);

  useEffect(() => {
    if (isUpdating) return;
    if (currentPathname) {
      getNoficationList();
    }
  }, [currentPathname, getNoficationList, isUpdating]);

  useEffect(() => {
    const timerId = setInterval(getNoficationList, 1000 * 60 * 10);
    return () => clearInterval(timerId);
  }, [getNoficationList]);

  return (
    <div>
      <IconButton color="inherit" edge="start" onClick={handleClick}>
        <Badge color="secondary" variant="dot" invisible={!enabled}>
          <NotificationIcon />
        </Badge>
      </IconButton>
      <Popover
        open={!!anchorEl}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Paper className={classes.container}>
          <div className={classes.infos}>
            <div className={classes.names}>
              <Typography variant="h6" component="h2">
                Notifications
              </Typography>
            </div>
          </div>

          {enabled ? (
            <div>
              <List>
                {notifications.map((notification) => (
                  <ListItem
                    button
                    onClick={() => {
                      handleOnNotificationClick(notification);
                    }}
                    key={notification.date}
                  >
                    <ListItemAvatar>
                      <Avatar>
                        <ImageIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={notification.title}
                      secondary={`${notification.subtitle} - ${notification.date}`}
                    />
                  </ListItem>
                ))}
              </List>
              <div className={classes.action}>
                <Button color="secondary" onClick={handleOnDelete}>
                  Effacer les Notifications
                </Button>
              </div>
            </div>
          ) : (
            <Typography align="center" className={classes.empty}>
              Pas de notifications
            </Typography>
          )}
        </Paper>
      </Popover>
    </div>
  );
};

export default Notifications;
